/**
 * Parser-based defense for freeform TEMPLATE html (the pipeline-authored,
 * editor-immutable markup that carries data-edit markers).
 *
 * Two failure modes this closes:
 *  1. Active content in a "trusted" template — event handlers, <script>,
 *     <style>, <iframe>, srcdoc, off-site/js: URLs. The legacy WP-extraction
 *     path (splitLegacy) ingests real markup, so templates are NOT inherently
 *     trustworthy; we sanitize on migration and REJECT on validation.
 *  2. A data-edit marker placed on an element where later text insertion is
 *     unsafe. cheerio's serializer does NOT entity-encode the text children
 *     of raw-text elements (<style>, <script>, <textarea>, <title>, <xmp>,
 *     <noscript>, <iframe>), so `.text('</style><script>…')` under a <style>
 *     breaks out. Markers are only allowed on safe host elements per type.
 */
import * as cheerio from 'cheerio';
import type { AnyNode, Element } from 'domhandler';
import { decodeCssEscapes, isAllowedEmbedSrc, isAllowedFormAction, isAllowedUrl } from './index.js';

/**
 * Elements removed wholesale from a freeform template. Note: `iframe` and
 * `svg` are NOT here — brochure sites legitimately embed maps/videos and
 * inline SVG icons. They are cleaned specially: iframe by an embed-host
 * allowlist, svg by removing its dangerous descendants (script/foreignobject
 * are in this set, so the general walk strips them wherever they appear).
 */
const FORBIDDEN_TAGS = new Set([
  'script', 'style', 'object', 'embed', 'noscript', 'noembed', 'noframes',
  'template', 'base', 'meta', 'link', 'form', 'applet', 'frame', 'frameset',
  'math', 'xmp', 'plaintext', 'portal', 'foreignobject',
  // SVG SMIL animation can mutate a parent <a>'s href to javascript: at
  // runtime (a static-href allowlist can't catch that) — forbid it outright.
  'animate', 'animatetransform', 'animatemotion', 'animatecolor', 'set', 'mpath',
]);

/** Raw-text / RCDATA / void elements: unsafe hosts for a text/richtext marker. */
const UNSAFE_MARKER_HOSTS = new Set([
  'style', 'script', 'textarea', 'title', 'xmp', 'noscript', 'iframe', 'noembed', 'plaintext',
  'br', 'hr', 'img', 'input', 'area', 'base', 'col', 'embed', 'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

/** URL-bearing attributes validated against the canonical URL policy. */
const URL_ATTRS = ['href', 'src', 'action', 'formaction', 'poster', 'data', 'background', 'cite', 'xlink:href'];

/**
 * Choose the right URL policy per (tag, attr): form actions must be
 * same-origin (not just any https), and an <svg><use> reference must be
 * same-origin or a same-document fragment. Everything else uses the general
 * content-URL policy.
 */
function urlAttrAllowed(tag: string, attr: string, value: string): boolean {
  if (attr === 'action' || attr === 'formaction') return isAllowedFormAction(value);
  if (tag === 'use' && (attr === 'href' || attr === 'xlink:href')) {
    return value.trim().startsWith('#') || isAllowedFormAction(value);
  }
  return isAllowedUrl(value);
}

export interface HtmlIssue {
  code:
    | 'forbidden-element'
    | 'forbidden-embed'
    | 'event-handler'
    | 'srcdoc'
    | 'unsafe-url'
    | 'unsafe-style';
  detail: string;
}

function styleIsUnsafe(style: string): boolean {
  // Decode CSS escapes first so `u\72l(...)` / `expr\65 ssion` can't hide.
  const compact = decodeCssEscapes(style).replace(/\s+/g, '').toLowerCase();
  return (
    compact.includes('url(') ||
    compact.includes('expression') ||
    compact.includes('javascript:') ||
    compact.includes('@import') ||
    compact.includes('/*')
  );
}

function isElement(node: AnyNode): node is Element {
  return node.type === 'tag' || node.type === 'script' || node.type === 'style';
}

/**
 * Walk a loaded template, collecting issues and (when `mutate`) removing the
 * offending elements/attributes in place. Shared by check + sanitize.
 */
function scrubAttributes(node: Element, tag: string, mutate: boolean, issues: HtmlIssue[]): void {
  for (const attrName of Object.keys(node.attribs)) {
    const lower = attrName.toLowerCase();
    const rawValue = node.attribs[attrName] ?? '';
    // iframe src is validated separately (embed allowlist), not by isAllowedUrl.
    const isIframeSrc = tag === 'iframe' && lower === 'src';
    if (lower.startsWith('on')) {
      issues.push({ code: 'event-handler', detail: `${attrName} handler on <${tag}>` });
      if (mutate) delete node.attribs[attrName];
    } else if (lower === 'srcdoc') {
      issues.push({ code: 'srcdoc', detail: `srcdoc on <${tag}>` });
      if (mutate) delete node.attribs[attrName];
    } else if (!isIframeSrc && URL_ATTRS.includes(lower) && rawValue.trim() !== '' && !urlAttrAllowed(tag, lower, rawValue)) {
      issues.push({ code: 'unsafe-url', detail: `${attrName}="${rawValue.slice(0, 60)}" on <${tag}>` });
      if (mutate) delete node.attribs[attrName];
    } else if (lower === 'style' && styleIsUnsafe(rawValue)) {
      issues.push({ code: 'unsafe-style', detail: `style on <${tag}>` });
      if (mutate) delete node.attribs[attrName];
    }
  }
}

function scrub($: cheerio.CheerioAPI, mutate: boolean, allowForm = false): HtmlIssue[] {
  const issues: HtmlIssue[] = [];
  const removeElements: Element[] = [];

  $('*').each((_, node) => {
    if (!isElement(node)) return;
    const tag = node.tagName.toLowerCase();
    if (FORBIDDEN_TAGS.has(tag) && !(allowForm && tag === 'form')) {
      issues.push({ code: 'forbidden-element', detail: `<${tag}> is not allowed in a freeform template` });
      if (mutate) removeElements.push(node);
      return;
    }
    // An iframe from a non-allowlisted embed host is removed entirely.
    if (tag === 'iframe' && !isAllowedEmbedSrc(node.attribs.src ?? '')) {
      issues.push({ code: 'forbidden-embed', detail: `iframe src "${(node.attribs.src ?? '').slice(0, 60)}" is not an allowed embed` });
      if (mutate) removeElements.push(node);
      return;
    }
    scrubAttributes(node, tag, mutate, issues);
  });

  if (mutate) for (const element of removeElements) $(element).remove();
  return issues;
}

/** Non-mutating: issues if a freeform template contains active/unsafe content. */
export function checkFreeformTemplate(html: string): HtmlIssue[] {
  return scrub(cheerio.load(html, null, false), false);
}

/**
 * Defense-in-depth check for MODULE view output before it hits the raw
 * freeform sink. Same rules as a freeform template EXCEPT a <form> is allowed
 * (forms.contact legitimately emits one) — everything active/unsafe (script,
 * handlers, unsafe URLs, non-allowlisted iframes, SMIL) is still rejected.
 */
export function checkModuleOutput(html: string): HtmlIssue[] {
  return scrub(cheerio.load(html, null, false), false, true);
}

/** Mutating: return a sanitized copy of a freeform template (used on migration). */
export function sanitizeFreeformTemplate(html: string): string {
  const $ = cheerio.load(html, null, false);
  scrub($, true);
  return $.html();
}

/**
 * Guarantee the freeform root element carries `id={anchor}` so in-page nav
 * (`#anchor`) resolves. Used on migration; hand-authored definitions are
 * instead required to include it (validateDefinition fails loud otherwise).
 */
export function ensureRootAnchorId(html: string, anchor: string): string {
  const $ = cheerio.load(html, null, false);
  const root = $.root().children().first();
  if (root.length === 1 && root.attr('id') !== anchor) {
    root.attr('id', anchor);
    return $.html();
  }
  return html;
}

export interface MarkerPlacementIssue {
  code: 'marker-count' | 'marker-unsafe-host' | 'marker-wrong-element';
  fieldId: string;
  detail: string;
}

/** Field types that write text/markup into an element's children. */
const TEXTUAL_TYPES = new Set(['text', 'richtext']);

/**
 * Every manifest entry must resolve to exactly one marker on a host element
 * that is SAFE for the declared field type. This is what makes text/richtext
 * insertion safe regardless of where the template author placed the marker.
 */
export function checkMarkerPlacement(
  html: string,
  manifest: readonly { fieldId: string; type: string }[],
): MarkerPlacementIssue[] {
  const $ = cheerio.load(html, null, false);
  const issues: MarkerPlacementIssue[] = [];
  for (const entry of manifest) {
    const nodes = $(`[data-edit="${entry.fieldId}"]`);
    if (nodes.length !== 1) {
      issues.push({ code: 'marker-count', fieldId: entry.fieldId, detail: `expected 1 marker, found ${nodes.length}` });
      continue;
    }
    const node = nodes.get(0);
    if (!node || !isElement(node)) continue;
    const tag = node.tagName.toLowerCase();
    if (TEXTUAL_TYPES.has(entry.type)) {
      if (UNSAFE_MARKER_HOSTS.has(tag)) {
        issues.push({ code: 'marker-unsafe-host', fieldId: entry.fieldId, detail: `${entry.type} marker on unsafe <${tag}>` });
      }
    } else if (entry.type === 'image' && tag !== 'img') {
      issues.push({ code: 'marker-wrong-element', fieldId: entry.fieldId, detail: `image marker must be on <img>, found <${tag}>` });
    } else if (entry.type === 'link' && tag !== 'a') {
      issues.push({ code: 'marker-wrong-element', fieldId: entry.fieldId, detail: `link marker must be on <a>, found <${tag}>` });
    }
  }
  return issues;
}
